import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Smartphone, KeyRound } from "lucide-react";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Input } from "@/components/ui/input";
import { useStore } from "@/context/StoreContext";
import { api, errMsg } from "@/lib/api";

export const OtpLogin = () => {
  const { login } = useStore();
  const [phone, setPhone] = useState("");
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [wait, setWait] = useState(0);
  const [busy, setBusy] = useState(false);
  const [channel, setChannel] = useState("");

  useEffect(() => {
    if (wait <= 0) return;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/otp/send", { phone });
      setSent(true); setWait(r.data.resend_after); setDevOtp(r.data.dev_otp || ""); setCode(""); setChannel(r.data.channel);
      toast.success(r.data.channel === "whatsapp" ? "OTP sent on WhatsApp" : r.data.channel === "sms" ? "OTP sent by SMS" : "OTP sent");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const verify = async (c) => {
    setBusy(true);
    try {
      const r = await api.post("/auth/otp/verify", { phone, code: c });
      login(r.data.token, r.data.customer);
      toast.success("Phone verified");
    } catch (e) { toast.error(errMsg(e)); setCode(""); } finally { setBusy(false); }
  };

  if (!sent) {
    return (
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="space-y-3" data-testid="otp-phone-form">
        <div className="flex items-center gap-2 rounded-full border border-stone-200 bg-white h-12 pl-4 focus-within:ring-2 focus-within:ring-primary/40">
          <Smartphone className="w-4 h-4 text-stone-400" strokeWidth={1.5} />
          <span className="text-stone-500 font-semibold">+91</span>
          <Input value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))} inputMode="numeric"
            placeholder="10-digit mobile number" className="border-0 shadow-none focus-visible:ring-0 h-11 text-base" data-testid="otp-phone-input" />
        </div>
        <button disabled={busy || phone.length !== 10} className="h-12 px-8 rounded-full bg-primary text-white font-bold disabled:opacity-40 active:scale-95 transition-transform" data-testid="otp-send-btn">
          {busy ? "Sending…" : "Send OTP"}
        </button>
      </form>
    );
  }

  return (
    <div className="space-y-4" data-testid="otp-verify-form">
      <p className="text-sm text-stone-600">Enter the 6-digit code sent {channel === "whatsapp" ? "on WhatsApp" : channel === "sms" ? "by SMS" : ""} to <b>+91 {phone}</b>
        <button onClick={() => setSent(false)} className="ml-2 text-accent font-semibold underline-offset-2 hover:underline" data-testid="otp-change-phone-btn">Change</button>
      </p>
      {devOtp && (
        <div className="flex items-center gap-2 text-sm rounded-xl bg-accent/10 text-accent px-3 py-2" data-testid="otp-dev-code">
          <KeyRound className="w-4 h-4" strokeWidth={1.5} /> Test mode (SMS/WhatsApp not connected yet) — your OTP is <b className="tracking-widest">{devOtp}</b>
        </div>
      )}
      <InputOTP maxLength={6} value={code} onChange={setCode} onComplete={verify} disabled={busy} data-testid="otp-code-input">
        <InputOTPGroup className="gap-2">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <InputOTPSlot key={i} index={i} className="h-12 w-11 sm:h-14 sm:w-12 text-xl font-bold rounded-xl border bg-white first:rounded-xl last:rounded-xl" />
          ))}
        </InputOTPGroup>
      </InputOTP>
      <div className="flex items-center gap-4 text-sm">
        <button onClick={() => verify(code)} disabled={busy || code.length !== 6} className="h-11 px-6 rounded-full bg-primary text-white font-bold disabled:opacity-40 active:scale-95 transition-transform" data-testid="otp-verify-btn">
          Verify
        </button>
        <button onClick={send} disabled={wait > 0 || busy} className="text-primary font-semibold disabled:text-stone-400" data-testid="otp-resend-btn">
          {wait > 0 ? `Resend in ${wait}s` : "Didn't get it? Resend"}
        </button>
      </div>
    </div>
  );
};
